// ImageBlockEditor — single image with caption / alt text / width / align.
//
// Two ways to source the image:
//   1. Uploaded file → block.file (filename in media_assets)
//   2. External URL → block.url (Pinterest/Unsplash/etc. — preferred for
//      rights-sensitive curation)
//
// Drop-zone behavior:
//   • Empty state: HTML5 drag-drop accepts an image, or click to open MediaPicker.
//   • There's also a URL field for entering an external image.
//   • Populated state: shows the image; clicking opens MediaPicker to swap it.
//
// File upload is delegated to parent (onUploadFile) — the parent does
// the PB FormData PATCH with `media_assets+` notation and returns the
// saved filename for storage in the block.

import { useState } from "react";
import type { ImageBlock, Block, Align, MediaWidth } from "../../../lib/blocks";
import { fileUrl } from "../../../lib/pb";
import { MediaPicker } from "../MediaPicker";
import { t } from "../theme";

export interface ImageBlockEditorProps {
  block: ImageBlock;
  onChange: (patch: Partial<Block>) => void;
  isSelected: boolean;
  articleId: string;
  mediaAssets: string[];
  onUploadFile: (file: File) => Promise<string>;
}

const WIDTHS: MediaWidth[] = ["narrow", "regular", "wide", "full"];

export function ImageBlockEditor({
  block,
  onChange,
  isSelected,
  articleId,
  mediaAssets,
  onUploadFile,
}: ImageBlockEditorProps) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [drag, setDrag] = useState(false);
  const [uploadErr, setUploadErr] = useState<string | null>(null);

  async function handleDropFile(file: File | null) {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setUploadErr("Only image files");
      return;
    }
    setUploadErr(null);
    try {
      const filename = await onUploadFile(file);
      // file replaces url
      onChange({ file: filename, url: undefined } as Partial<ImageBlock>);
    } catch (e) {
      setUploadErr((e as Error).message);
    }
  }

  const hasMedia = !!(block.file || block.url);
  const previewSrc = block.file
    ? fileUrl("articles", articleId, block.file, "960x540")
    : block.url || "";

  return (
    <div>
      {isSelected && hasMedia && (
        <div style={{ display: "flex", gap: 6, marginBottom: 8, fontFamily: t.sans, flexWrap: "wrap" }}>
          {WIDTHS.map((w) => (
            <button
              key={w}
              type="button"
              onClick={() => onChange({ width: w } as Partial<ImageBlock>)}
              style={pillStyle((block.width || "regular") === w)}
            >
              {w}
            </button>
          ))}
          <div style={{ width: 8 }} />
          {(["left", "center", "right"] as Align[]).map((a) => (
            <button
              key={a}
              type="button"
              onClick={() => onChange({ align: a } as Partial<ImageBlock>)}
              style={pillStyle((block.align || "center") === a)}
            >
              {a}
            </button>
          ))}
        </div>
      )}

      {hasMedia ? (
        <div
          style={{
            display: "flex",
            justifyContent:
              block.align === "left"
                ? "flex-start"
                : block.align === "right"
                ? "flex-end"
                : "center",
          }}
        >
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            style={{
              padding: 0,
              border: "none",
              background: "transparent",
              cursor: "pointer",
              width: widthToCSS(block.width || "regular"),
              maxWidth: "100%",
            }}
            title="Click to replace"
          >
            <img
              src={previewSrc}
              alt={block.alt || ""}
              style={{
                width: "100%",
                display: "block",
                borderRadius: 4,
                background: t.cardRaised,
              }}
            />
          </button>
        </div>
      ) : (
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDrag(true);
          }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDrag(false);
            const f = e.dataTransfer.files?.[0];
            void handleDropFile(f || null);
          }}
          onClick={() => setPickerOpen(true)}
          style={{
            padding: "40px 20px",
            borderRadius: 8,
            border: `2px dashed ${drag ? t.accent : t.border}`,
            background: drag ? t.accentSoft : t.bg,
            color: t.text3,
            textAlign: "center",
            cursor: "pointer",
            fontFamily: t.sans,
            fontSize: 13,
            transition: t.transition,
          }}
        >
          <div style={{ fontSize: 20, color: t.text4, marginBottom: 6 }}>▢</div>
          Drop an image here, click to choose, or paste a URL below
        </div>
      )}

      {uploadErr && (
        <div
          style={{
            marginTop: 6,
            fontSize: 11,
            color: t.danger,
            fontFamily: t.sans,
          }}
        >
          {uploadErr}
        </div>
      )}

      {isSelected && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            marginTop: 8,
            fontFamily: t.sans,
          }}
        >
          <input
            type="url"
            value={block.url || ""}
            onChange={(e) => {
              const url = e.target.value;
              // URL takes priority — clear file when URL is being typed.
              onChange({ url, file: undefined } as Partial<ImageBlock>);
            }}
            placeholder="External image URL (https://…)"
            style={inputStyle}
          />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <input
              type="text"
              value={block.alt || ""}
              onChange={(e) => onChange({ alt: e.target.value } as Partial<ImageBlock>)}
              placeholder="Alt text"
              style={inputStyle}
            />
            <input
              type="text"
              value={block.caption || ""}
              onChange={(e) =>
                onChange({ caption: e.target.value } as Partial<ImageBlock>)
              }
              placeholder="Caption"
              style={inputStyle}
            />
          </div>
        </div>
      )}

      {block.caption && !isSelected && (
        <div
          style={{
            marginTop: 6,
            fontSize: 12,
            color: t.text3,
            fontStyle: "italic",
            textAlign: "center",
            fontFamily: t.serif,
          }}
        >
          {block.caption}
        </div>
      )}

      {pickerOpen && (
        <MediaPicker
          articleId={articleId}
          mediaAssets={mediaAssets}
          accept="image"
          onPick={(filename) => {
            onChange({ file: filename, url: undefined } as Partial<ImageBlock>);
            setPickerOpen(false);
          }}
          onClose={() => setPickerOpen(false)}
          onUploadFile={onUploadFile}
        />
      )}
    </div>
  );
}

function widthToCSS(w: MediaWidth): string {
  switch (w) {
    case "narrow": return "440px";
    case "regular": return "640px";
    case "wide": return "100%";
    case "full": return "100%";
  }
}

function pillStyle(active: boolean): React.CSSProperties {
  return {
    padding: "3px 8px",
    fontSize: 11,
    borderRadius: 4,
    background: active ? t.accentSoft : "transparent",
    color: active ? t.accent : t.text3,
    border: `1px solid ${active ? t.accentStrong : t.border}`,
    cursor: "pointer",
    textTransform: "capitalize",
  };
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  padding: "6px 8px",
  background: t.bg,
  border: `1px solid ${t.border}`,
  borderRadius: 4,
  fontSize: 12,
  color: t.text,
  outline: "none",
  fontFamily: t.sans,
};
